import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { ClipLoader } from 'react-spinners';
import { toast, ToastContainer } from 'react-toastify';
import { motion } from 'framer-motion';
import 'react-toastify/dist/ReactToastify.css';
import newsessionimage from '../newsessionimage.webp';
import resumelastsessionimage from '../resumelastsessionimage.webp';
import journalimage from '../journalimage.webp';
import meditationimage from '../meditationimage.webp';
import chatroomimage from '../chatroomimage.webp';
import quickreferenceimage from '../quickreferenceimage.webp';
import cardbackgroundimage from '../cardbackground.webp';
import cardbackground from '../cardbackground.webp';
import buttonImage from '../buttonimage.webp';
import companionimage from '../companionimage.webp';
// eslint-disable-next-line no-unused-vars
import todoimage from '../todoimage.webp';
import astrologyimage from '../astrologyimage.webp';
import knowthyselfimage from '../knowthyselfimage.webp';
import qaimage from '../qaimage.webp';
import oracleimage from '../oracleimage.webp';
import juliecam from '../juliecamera.webp';
import rightforyouimage from '../rightforyouimage.webp';

const Home = ({ goToPage, currentPage }) => {
  const [todos, setTodos] = useState([]);
  const [newTask, setNewTask] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  const [greetingName, setGreetingName] = useState(''); // State for the user's name
  const [cardDescription, setCardDescription] = useState('Live stream unavailable'); // Dynamic description
  // eslint-disable-next-line no-unused-vars
  const [isLive, setIsLive] = useState(false); // Define isLive for JulieCam card
  const [textColor, setTextColor] = useState('#4B5563'); // New state for text color
  const [isBold, setIsBold] = useState(false); // New state for bold

  const retrySupabaseQuery = async (queryFn, retries = 3, delay = 1000) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        return await queryFn();
      } catch (error) {
        if (attempt < retries) {
          console.warn(`Retrying query (attempt ${attempt})...`);
          await new Promise((resolve) => setTimeout(resolve, delay));
        } else {
          throw error;
        }
      }
    }
  };

  const fetchGreetingName = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error || !user) {
        console.error('Error fetching user:', error?.message || 'User not found');
        if (currentPage === 'home') {
          toast.error('No user found.');
        }
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('nickname, first_name')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError.message);
        return;
      }

      setGreetingName(profile.nickname || profile.first_name || 'User');
    } catch (err) {
      console.error('Unexpected error fetching greeting name:', err.message);
    }
  };

  const fetchTodos = async () => {
    try {
      setIsLoading(true);
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        console.error('No user found:', userError?.message);
        if (currentPage === 'home') {
          toast.error('No user found.');
        }
        return;
      }

      const { data, error, status } = await retrySupabaseQuery(() =>
        supabase
          .rpc('decrypt_todo_task')
      );

      if (error) {
        console.error('Error fetching todos:', error.code, error.message);
        if (status === 403) {
          toast.error('Permission denied: ' + error.message);
        }
        throw error;
      }

      setTodos(data || []);
    } catch (err) {
      toast.error('Failed to load todos.');
      console.error('Error fetching todos:', err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStreamConfig = async () => {
    try {
      const { data, error } = await supabase
        .schema('api')
        .from('stream_config')
        .select('card_description, stream_id, text_color, is_bold')
        .eq('id', 1)
        .maybeSingle();
      if (error) throw error;
      if (data) {
        setCardDescription(data.card_description || 'Live stream unavailable');
        setIsLive(!!data.stream_id);
        setTextColor(data.text_color || '#4B5563');
        setIsBold(data.is_bold || false);
      } else {
        setCardDescription('Live stream unavailable');
        setIsLive(false);
        setTextColor('#4B5563');
        setIsBold(false);
      }
    } catch (error) {
      console.error('Error fetching stream config:', error);
      setCardDescription('Live stream unavailable');
      setIsLive(false);
      setTextColor('#4B5563');
      setIsBold(false);
    }
  };

  const addTodo = async () => {
    if (!newTask.trim()) {
      toast.error('Please enter a task.');
      return;
    }

    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        toast.error('User not logged in!');
        return;
      }

      const { data, error } = await retrySupabaseQuery(() =>
        supabase
          .from('todos')
          .insert([{ task: newTask.trim(), complete: false, user_id: user.id }])
          .select('id, user_id, complete, created_at, encrypted_task')
          .single()
      );

      if (error) {
        console.error('Insert error:', error.message);
        toast.error('Failed to add todo: ' + error.message);
        throw error;
      }

      // Fetch decrypted task
      const { data: decryptedData, error: decryptError } = await supabase
        .rpc('decrypt_todo_task')
        .eq('id', data.id)
        .single();
      if (decryptError) {
        console.error('Decrypt error for new todo:', decryptError.message);
        toast.error('Todo saved, but failed to display: ' + decryptError.message);
        return;
      }

      setTodos((prev) => [decryptedData, ...prev]);
      setNewTask('');
      toast.success('Todo added successfully!');
    } catch (err) {
      toast.error('Failed to add todo.');
      console.error('Error adding todo:', err.message);
    }
  };

  const updateTodo = async (id, complete) => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        toast.error('User not logged in!');
        return;
      }

      const { error } = await retrySupabaseQuery(() =>
        supabase
          .from('todos')
          .update({ complete })
          .eq('id', id)
          .eq('user_id', user.id)
      );

      if (error) {
        console.error('Update error:', error.message);
        toast.error('Failed to update todo: ' + error.message);
        throw error;
      }

      setTodos((prev) =>
        prev.map((todo) => (todo.id === id ? { ...todo, complete } : todo))
      );
      toast.success('Todo updated!');
    } catch (err) {
      toast.error('Failed to update todo.');
      console.error('Error updating todo:', err.message);
    }
  };

  const deleteTodo = async (id) => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        toast.error('User not logged in!');
        return;
      }

      const { error } = await retrySupabaseQuery(() =>
        supabase
          .from('todos')
          .delete()
          .eq('id', id)
          .eq('user_id', user.id)
      );

      if (error) {
        console.error('Delete error:', error.message);
        toast.error('Failed to delete todo: ' + error.message);
        throw error;
      }

      setTodos((prev) => prev.filter((todo) => todo.id !== id));
      toast.success('Todo deleted successfully!');
    } catch (err) {
      toast.error('Failed to delete todo.');
      console.error('Error deleting todo:', err.message);
    }
  };

  // Update resumeLastSession to check for animated_helper and pass correct info to goToPage
  const resumeLastSession = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        console.error('Error fetching user:', userError);
        toast.error('User not logged in!');
        return;
      }

      // Fetch animated_helper, current_question_id, and current_component
      const { data: progress, error: progressError } = await supabase
        .from('user_progress')
        .select('current_question_id, current_component, animated_helper')
        .eq('user_id', user.id)
        .order('timestamp', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (progressError || !progress) {
        console.error('Error fetching progress:', progressError?.message || 'No progress found');
        toast.error('No Session Saved. Please Start a New Session!');
        return;
      }

      // If animated_helper is 'yes', pass info to goToPage for AnimatedHelper logic in App.js
      if (progress.animated_helper === 'yes') {
        // Detect device type at the time of resume
        const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
        goToPage({
          component: progress.current_component,
          questionId: progress.current_question_id,
          animatedHelper: true,
          isMobile // Pass this value
        });
      } else if (progress.current_component && progress.current_component !== null) {
        localStorage.setItem('resumeQuestionId', progress.current_question_id);
        const componentName = progress.current_component === 'SelfCare' ? 'selfCare' : progress.current_component;
        goToPage(componentName);
      } else {
        toast.error('No Session Saved. Please Start a New Session!');
      }
    } catch (err) {
      console.error('Unexpected error fetching progress:', err);
      toast.error('An unexpected error occurred while resuming the session.');
    }
  };

  useEffect(() => {
    fetchGreetingName();
    fetchTodos();
    fetchStreamConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      style={{
        backgroundImage: `url(${cardbackgroundimage})`,
        backgroundSize: 'cover',
        minHeight: '100vh',
        padding: '20px',
        paddingBottom: '110px', // Use paddingBottom instead of marginBottom
        boxSizing: 'border-box',
      }}
    >
      {/* Add this style block for mobile image sizing */}
      <style>
        {`
          @media (max-width: 600px) {
            .home-card-img {
              min-height: 150px !important;
              height: 150px !important;
              object-fit: cover !important;
              width: 100% !important;
              display: block !important;
            }
          }
        `}
      </style>
      <ToastContainer />
      <h1 style={{ textAlign: 'center', fontFamily: 'Times New Roman, Times, serif', fontSize: '36px', fontWeight: 'bold' }}>
        Welcome, {greetingName}!
      </h1>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '20px', marginTop: '20px' }}>
        {/* New Session Card */}
        <div
          style={{
            width: '300px',
            borderRadius: '10px',
            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
            border: '2px solid black',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            backgroundColor: 'white',
            overflow: 'hidden',
          }}
        >
          <img
            src={newsessionimage}
            alt="New Session"
            className="home-card-img"
            style={{
              width: '100%',
              height: '180px', // Match Resume Last Session image height
              objectFit: 'cover',
              borderRadius: '10px 10px 0 0',
              display: 'block',
            }}
          />
          <div
            style={{
              backgroundImage: `url(${cardbackgroundimage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderBottomLeftRadius: '10px',
              borderBottomRightRadius: '10px',
              padding: '10px',
              textAlign: 'center',
              width: '100%',
              flex: 1,
              minHeight: window.innerWidth > 600 ? '110px' : undefined,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <button
              style={{
                width: '66.67%',
                padding: '10px',
                margin: '0 auto',
                fontFamily: 'Times New Roman, Times, serif',
                fontSize: '16px',
                fontWeight: 'bold',
                borderRadius: '5px',
                // backgroundColor: '#4B5563',
                backgroundImage: `url(${buttonImage})`,
                backgroundSize: 'cover',
                color: 'white',
                boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
              }}
              onClick={() => goToPage('newSession')}
            >
              New Session
            </button>
            <p
              style={{
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px',
                textAlign: 'center',
                color: '#4B5563',
              }}
            >
              A tool for moving through discomfort, and exploring your thoughts and feelings.
            </p>
          </div>
        </div>

        {/* Resume Last Session Card */}
        <div
          style={{
            width: '300px',
            borderRadius: '10px',
            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
            border: '2px solid black',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            backgroundColor: 'white',
          }}
        >
          <img
            src={resumelastsessionimage}
            alt="Resume Last Session"
            className="home-card-img"
            style={{
              width: '100%',
              height: '180px', // Reference height for all top row images
              objectFit: 'cover',
              borderRadius: '10px 10px 0 0',
              display: 'block',
            }}
          />
          <div
            style={{
              backgroundImage: `url(${cardbackgroundimage})`,
              backgroundSize: 'cover',
              borderBottomLeftRadius: '10px',
              borderBottomRightRadius: '10px',
              padding: '10px',
              textAlign: 'center',
            }}
          >
            <button
              style={{
                width: window.innerWidth <= 600 ? '90%' : '66.67%',
                minWidth: window.innerWidth <= 600 ? '180px' : undefined,
                padding: '10px',
                margin: '0 auto',
                fontFamily: 'Times New Roman, Times, serif',
                fontSize: '16px',
                fontWeight: 'bold',
                borderRadius: '5px',
                // backgroundColor: '#4B5563',
                backgroundImage: `url(${buttonImage})`,
                backgroundSize: 'cover',
                color: 'white',
                marginBottom: '10px',
                whiteSpace: 'normal',
                boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
              }}
              onClick={resumeLastSession}
            >
              Resume Last Session
            </button>
            <p
              style={{
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px',
                textAlign: 'center',
                color: '#4B5563',
              }}
            >
              Continue where you left off in your last saved session.
            </p>
          </div>
        </div>

        {/* Journal Card */}
        <div
          style={{
            width: '300px',
            borderRadius: '10px',
            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
            border: '2px solid black',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            backgroundColor: 'white',
            overflow: 'hidden',
          }}
        >
          <img
            src={journalimage}
            alt="Journal"
            className="home-card-img"
            style={{
              width: '100%',
              height: '180px', // Match Resume Last Session image height
              objectFit: 'cover',
              borderRadius: '10px 10px 0 0',
              display: 'block',
              margin: 0,
            }}
          />
          <div
            style={{
              backgroundImage: `url(${cardbackgroundimage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderBottomLeftRadius: '10px',
              borderBottomRightRadius: '10px',
              padding: '10px',
              textAlign: 'center',
              width: '100%',
              flex: 1,
              minHeight: window.innerWidth > 600 ? '110px' : undefined,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              marginTop: 0, // Remove any gap between image and background
            }}
          >
            <button
              style={{
                width: '66.67%',
                padding: '10px',
                margin: '0 auto',
                fontFamily: 'Times New Roman, Times, serif',
                fontSize: '16px',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '5px',
                // backgroundColor: '#4B5563',
                backgroundImage: `url(${buttonImage})`,
                backgroundSize: 'cover',
                color: 'white',
                marginBottom: '10px',
                boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
              }}
              onClick={() => goToPage('journal')}
            >
              Journal
            </button>
            <p
              style={{
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px',
                textAlign: 'center',
                color: '#4B5563',
              }}
            >
              Write down your thoughts and reflect.
            </p>
          </div>
        </div>

        {/* Meditation Card */}
        <div
          style={{
            width: '300px',
            borderRadius: '10px',
            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
            border: '2px solid black',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            backgroundColor: 'white',
          }}
        >
          <img
            src={meditationimage}
            alt="Meditation"
            className="home-card-img"
            style={{
              width: '100%',
              height: '180px', // Match Resume Last Session image height
              objectFit: 'cover',
              borderRadius: '10px 10px 0 0',
              display: 'block',
              margin: 0,
            }}
          />
          <div
            style={{
              backgroundImage: `url(${cardbackgroundimage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderBottomLeftRadius: '10px',
              borderBottomRightRadius: '10px',
              padding: '10px',
              textAlign: 'center',
              width: '100%',
              flex: 1,
              minHeight: window.innerWidth > 600 ? '110px' : undefined,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              marginTop: 0, // Remove any gap between image and background
            }}
          >
            <button
              style={{
                width: '66.67%',
                padding: '10px',
                margin: '0 auto',
                fontFamily: 'Times New Roman, Times, serif',
                fontSize: '16px',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '5px',
                // backgroundColor: '#4B5563',
                backgroundImage: `url(${buttonImage})`,
                backgroundSize: 'cover',
                color: 'white',
                marginBottom: '10px',
                boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
              }}
              onClick={() => goToPage('meditation')}
            >
              Meditation
            </button>
            <p
              style={{
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px',
                textAlign: 'center',
                color: '#4B5563',
              }}
            >
              Relax and center yourself.
            </p>
          </div>
        </div>

        {/* Community Chat Card */}
        <div
          style={{
            width: '300px',
            borderRadius: '10px',
            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
            border: '2px solid black',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            backgroundColor: 'white',
          }}
        >
          <img
            src={chatroomimage}
            alt="Community Chat"
            className="home-card-img"
            style={{
              width: '100%',
              height: '180px',
              objectFit: 'cover',
              borderRadius: '10px 10px 0 0',
              display: 'block',
            }}
          />
          <div
            style={{
              backgroundImage: `url(${cardbackgroundimage})`,
              backgroundSize: 'cover',
              borderBottomLeftRadius: '10px',
              borderBottomRightRadius: '10px',
              padding: '10px',
              textAlign: 'center',
            }}
          >
            <button
              style={{
                width: '66.67%',
                padding: '10px',
                margin: '0 auto',
                fontFamily: 'Times New Roman, Times, serif',
                fontSize: '16px',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '5px',
                // backgroundColor: '#4B5563',
                backgroundImage: `url(${buttonImage})`,
                backgroundSize: 'cover',
                color: 'white',
                marginBottom: '10px',
                boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
              }}
              onClick={() => goToPage('chat')}
            >
              Community Chat
            </button>
            <p
              style={{
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px',
                textAlign: 'center',
                color: '#4B5563',
              }}
            >
              Connect with others in the community chat room.
            </p>
          </div>
        </div>

        {/* Quick Reference Card */}
        <div
          style={{
            width: '300px',
            borderRadius: '10px',
            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
            border: '2px solid black',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            backgroundColor: 'white',
            overflow: 'hidden',
          }}
        >
          <img
            src={quickreferenceimage}
            alt="Quick Reference"
            className="home-card-img"
            style={{
              width: '100%',
              height: '180px',
              objectFit: 'cover',
              borderRadius: '10px 10px 0 0',
              display: 'block',
              margin: 0,
            }}
          />
          <div
            style={{
              backgroundImage: `url(${cardbackgroundimage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderBottomLeftRadius: '10px',
              borderBottomRightRadius: '10px',
              padding: '10px',
              textAlign: 'center',
              width: '100%',
              flex: 1,
              minHeight: window.innerWidth > 600 ? '110px' : undefined,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              marginTop: 0, // Remove any gap between image and background
            }}
          >
            <button
              style={{
                width: '66.67%',
                padding: '10px',
                margin: '0 auto',
                fontFamily: 'Times New Roman, Times, serif',
                fontSize: '16px',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '5px',
                // backgroundColor: '#4B5563',
                backgroundImage: `url(${buttonImage})`,
                backgroundSize: 'cover',
                color: 'white',
                marginBottom: '10px',
                boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
              }}
              onClick={() => goToPage('healingFormulasPage')}
            >
              Quick Reference
            </button>
            <p
              style={{
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px',
                textAlign: 'center',
                color: '#4B5563',
              }}
            >
              Access tools and knowledge.
            </p>
          </div>
        </div>

        {/* Companion Card */}
        <div
          style={{
            width: '300px',
            borderRadius: '10px',
            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
            border: '2px solid black',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `url(${cardbackgroundimage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              zIndex: 0,
            }}
          ></div>
          <img
            src={companionimage}
            alt="Companion"
            className="home-card-img"
            style={{
              width: '100%',
              height: '180px', // Match Astrology card image height
              objectFit: 'cover',
              borderRadius: '10px 10px 0 0',
              zIndex: 1,
              position: 'relative',
              display: 'block',
            }}
          />
          <div
            style={{
              padding: '10px',
              textAlign: 'center',
              zIndex: 1,
              position: 'relative',
            }}
          >
            <button
              style={{
                width: '66.67%',
                padding: '10px',
                margin: '0 auto',
                fontFamily: 'Times New Roman, Times, serif',
                fontSize: '16px',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '5px',
                // backgroundColor: '#4B5563',
                backgroundImage: `url(${buttonImage})`,
                backgroundSize: 'cover',
                color: 'white',
                marginBottom: '10px',
                boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
              }}
              onClick={() => goToPage('companion')}
            >
              Companion
            </button>
            <p
              style={{
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px',
                textAlign: 'center',
                color: '#4B5563',
              }}
            >
              A friendly simulated text chat for reflecting on lighter topics with transitions to deeper topics offered.
            </p>
          </div>
        </div>

        {/* To-Do List Card */}
        <div
          style={{
            width: '300px',
            borderRadius: '10px',
            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
            border: '2px solid black',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            backgroundColor: 'white',
            overflow: 'hidden',
            padding: 0,
            minHeight: '340px', // Ensure enough height for spacing
          }}
        >
          <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <h2 style={{ fontFamily: 'Times New Roman, Times, serif', fontSize: '18px', fontWeight: 'bold', color: '#4B5563' }}>
              To-Do List
            </h2>
            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
                <ClipLoader size={50} color="#4B5563" />
              </div>
            ) : (
              <div style={{ marginBottom: '16px' }}>
                {todos.map((todo) => (
                  <motion.div
                    key={todo.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: '1px solid #ccc',
                      padding: '4px 0 0 0', // Only top padding, no bottom
                      margin: 0,
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '14px',
                      color: todo.complete ? '#888' : '#000',
                      textDecoration: todo.complete ? 'line-through' : 'none',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <motion.input
                        type="checkbox"
                        checked={todo.complete}
                        onChange={(e) => updateTodo(todo.id, e.target.checked)}
                        whileTap={{ scale: 1.2 }}
                      />
                      {todo.task}
                    </label>
                    <button
                      type="button"
                      aria-label={`Delete task '${todo.task}'`}
                      style={{
                        color: 'inherit',
                        cursor: 'pointer',
                        fontSize: '12px',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                        fontWeight: 600,
                      }}
                      onClick={() => deleteTodo(todo.id)}
                    >
                      <span style={{ color: '#F72222' }}>delete</span>
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
            <input
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              placeholder="Add a new task"
              style={{
                padding: '10px',
                width: '100%',
                borderRadius: '5px',
                border: '1px solid #ccc',
                marginBottom: '30px', // Add more space below the input
                boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
                marginLeft: 0,
                marginRight: 0,
                boxSizing: 'border-box',
                display: 'block',
              }}
            />
            {/* Spacer to push the Add button to the bottom */}
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginBottom: 0 }}>
              <button
                onClick={addTodo}
                style={{
                  width: '100%',
                  maxWidth: '120px',
                  padding: '10px 0',
                  borderRadius: '5px',
                  fontFamily: 'Times New Roman, Times, serif',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  border: 'none',
                  backgroundImage: `url(${buttonImage})`,
                  backgroundSize: 'cover',
                  color: 'white',
                  display: 'block',
                  boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
                  margin: 0,
                }}
              >
                Add
              </button>
            </div>
          </div>
        </div>

        {/* Astrology Card */}
        <div
          style={{
            width: '300px',
            borderRadius: '10px',
            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
            border: '2px solid black',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            backgroundColor: 'white',
            overflow: 'hidden',
          }}
        >
          <img
            src={astrologyimage}
            alt="Astrology"
            className="home-card-img"
            style={{
              width: '100%',
              height: '180px', // Reference height for both Astrology and Companion images
              objectFit: 'cover',
              borderRadius: '10px 10px 0 0',
              display: 'block',
            }}
          />
          <div
            style={{
              backgroundImage: `url(${cardbackgroundimage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderBottomLeftRadius: '10px',
              borderBottomRightRadius: '10px',
              padding: '10px',
              textAlign: 'center',
              width: '100%',
              flex: 1,
              minHeight: window.innerWidth > 600 ? '110px' : undefined,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <button
              style={{
                width: '66.67%',
                padding: '10px',
                margin: '0 auto',
                fontFamily: 'Times New Roman, Times, serif',
                fontSize: '16px',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '5px',
                // backgroundColor: '#4B5563',
                backgroundImage: `url(${buttonImage})`,
                backgroundSize: 'cover',
                color: 'white',
                marginBottom: '10px',
                boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
              }}
              onClick={() => goToPage('astrology')}
            >
              Astrology
            </button>
            <p
              style={{
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px',
                textAlign: 'center',
                color: '#4B5563',
              }}
            >
              Reflect on basic astrological insights and astrology oracle.
            </p>
          </div>
        </div>

        {/* Know Thyself Card */}
        <div
          style={{
            width: '300px',
            borderRadius: '10px',
            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
            border: '2px solid black',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            backgroundColor: 'white',
            overflow: 'hidden',
          }}
        >
          <img
            src={knowthyselfimage}
            alt="Know Thyself"
            className="home-card-img"
            style={{
              width: '100%',
              height: '180px',
              objectFit: 'cover',
              borderRadius: '10px 10px 0 0',
              display: 'block',
            }}
          />
          <div
            style={{
              backgroundImage: `url(${cardbackgroundimage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderBottomLeftRadius: '10px',
              borderBottomRightRadius: '10px',
              padding: '10px',
              textAlign: 'center',
              width: '100%',
              flex: 1,
              minHeight: window.innerWidth > 600 ? '110px' : undefined,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <button
              style={{
                width: '66.67%',
                padding: '10px',
                margin: '0 auto',
                fontFamily: 'Times New Roman, Times, serif',
                fontSize: '16px',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '5px',
                // backgroundColor: '#4B5563',
                backgroundImage: `url(${buttonImage})`,
                backgroundSize: 'cover',
                color: 'white',
                marginBottom: '10px',
                boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
              }}
              onClick={() => goToPage('knowThyself')}
            >
              Know Thyself
            </button>
            <p
              style={{
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px',
                textAlign: 'center',
                color: '#4B5563',
              }}
            >
              Some notes on what you have learned about yourself!
            </p>
          </div>
        </div>

        {/* Q & A Card */}
        <div
          style={{
            width: '300px',
            borderRadius: '10px',
            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
            border: '2px solid black',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            backgroundColor: 'white',
            overflow: 'hidden',
          }}
        >
          <img
            src={qaimage}
            alt="Q & A"
            className="home-card-img"
            style={{
              width: '100%',
              height: '180px',
              objectFit: 'cover',
              borderRadius: '10px 10px 0 0',
              display: 'block',
            }}
          />
          <div
            style={{
              backgroundImage: `url(${cardbackgroundimage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderBottomLeftRadius: '10px',
              borderBottomRightRadius: '10px',
              padding: '10px',
              textAlign: 'center',
              width: '100%',
              flex: 1,
              minHeight: window.innerWidth > 600 ? '110px' : undefined,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <button
              style={{
                width: '66.67%',
                padding: '10px',
                margin: '0 auto',
                fontFamily: 'Times New Roman, Times, serif',
                fontSize: '16px',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '5px',
                // backgroundColor: '#4B5563',
                backgroundImage: `url(${buttonImage})`,
                backgroundSize: 'cover',
                color: 'white',
                marginBottom: '10px',
                boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
              }}
              onClick={() => goToPage('qa')}
            >
              Q &amp; A
            </button>
            <p
              style={{
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px',
                textAlign: 'center',
                color: '#4B5563',
              }}
            >
              Ask questions and See answers by Julie for Reflection Purposes only.
            </p>
          </div>
        </div>

        {/* Oracle Card */}
        <div
          style={{
            width: '300px',
            borderRadius: '10px',
            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
            border: '2px solid black',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            backgroundColor: 'white',
            overflow: 'hidden',
          }}
        >
          <img
            src={oracleimage}
            alt="Oracle"
            className="home-card-img"
            style={{
              width: '100%',
              height: '180px',
              objectFit: 'cover',
              borderRadius: '10px 10px 0 0',
              display: 'block',
            }}
          />
          <div
            style={{
              backgroundImage: `url(${cardbackgroundimage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderBottomLeftRadius: '10px',
              borderBottomRightRadius: '10px',
              padding: '10px',
              textAlign: 'center',
              width: '100%',
              flex: 1,
              minHeight: window.innerWidth > 600 ? '110px' : undefined,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <button
              style={{
                width: '66.67%',
                padding: '10px',
                margin: '0 auto',
                fontFamily: 'Times New Roman, Times, serif',
                fontSize: '16px',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '5px',
                // backgroundColor: '#4B5563',
                backgroundImage: `url(${buttonImage})`,
                backgroundSize: 'cover',
                color: 'white',
                marginBottom: '10px',
                boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
              }}
              onClick={() => goToPage('oracle')}
            >
              Oracle
            </button>
            <p
              style={{
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px',
                textAlign: 'center',
                color: '#4B5563',
              }}
            >
              Use the Oracle for information to reflect on.
            </p>
          </div>
        </div>

        {/* JulieCam Card */}
        <div
          style={{
            width: '300px',
            borderRadius: '10px',
            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
            border: '2px solid black',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            backgroundColor: 'white',
          }}
        >
          <img
            src={juliecam}
            alt="JulieCam"
            className="home-card-img"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '10px 10px 0 0',
              display: 'block',
            }}
          />
          <div
            style={{
              backgroundImage: `url(${cardbackgroundimage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderBottomLeftRadius: '10px',
              borderBottomRightRadius: '10px',
              padding: '10px',
              textAlign: 'center',
              width: '100%',
              flex: 1,
              minHeight: window.innerWidth > 600 ? '110px' : undefined,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <button
              style={{
                width: '66.67%',
                padding: '10px',
                margin: '0 auto',
                fontFamily: 'Times New Roman, Times, serif',
                fontSize: '16px',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '5px',
                // backgroundColor: '#4B5563',
                backgroundImage: `url(${buttonImage})`,
                backgroundSize: 'cover',
                color: 'white',
                marginBottom: '10px',
                boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
              }}
              onClick={() => goToPage('julieCam')}
            >
              JulieCam
            </button>
            <p
              style={{
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px',
                textAlign: 'center',
                color: textColor,
                fontWeight: isBold ? 'bold' : 'normal',
              }}
            >
              {cardDescription}
            </p>
          </div>
        </div>

        {/* Tutorial Card */}
        <div
          style={{
            width: '300px',
            borderRadius: '10px',
            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
            border: '2px solid black',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            backgroundColor: 'white',
            overflow: 'hidden',
          }}
        >
          <img
            src={rightforyouimage}
            alt="Tutorial"
            className="home-card-img"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '10px 10px 0 0',
              display: 'block',
            }}
          />
          <div
            style={{
              backgroundImage: `url(${cardbackgroundimage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderBottomLeftRadius: '10px',
              borderBottomRightRadius: '10px',
              padding: '10px',
              textAlign: 'center',
              width: '100%',
              flex: 1,
              minHeight: window.innerWidth > 600 ? '110px' : undefined,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <button
              style={{
                width: '66.67%',
                padding: '10px',
                margin: '0 auto',
                fontFamily: 'Times New Roman, Times, serif',
                fontSize: '16px',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '5px',
                // backgroundColor: '#4B5563',
                backgroundImage: `url(${buttonImage})`,
                backgroundSize: 'cover',
                color: 'white',
                marginBottom: '10px',
                boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
              }}
              onClick={() => goToPage('rightForYou')}
            >
              Tutorial
            </button>
            <p
              style={{
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px',
                textAlign: 'center',
                color: '#4B5563',
              }}
            >
              Find out how to use this tool for your own self inquiry.
            </p>
          </div>
        </div>
      </div>

      {/* Sticky Footer with duplicated buttons */}
      <footer
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100vw',
          backgroundImage: `url(${cardbackground})`,
          backgroundSize: 'cover',
          border: '2px solid black',
          borderRadius: '10px 10px 0 0',
          boxShadow: '3px 3px 5px rgba(0, 0, 0, 0.3)',
          padding: '6px', // Reduced from 10px to 6px
          textAlign: 'center',
          zIndex: 1000,
          minHeight: '45px',
          display: 'flex',
          justifyContent: 'center',
          gap: '10px',
        }}
      >
        <button
          style={{
            padding: '10px 20px',
            fontFamily: 'Times New Roman, Times, serif',
            fontSize: '16px',
            fontWeight: 'bold',
            border: '2px solid black',
            borderRadius: '5px',
            // backgroundColor: '#4B5563',
            backgroundImage: `url(${buttonImage})`,
            backgroundSize: 'cover',
            color: 'white',
            marginRight: '10px',
            boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
          }}
          onClick={() => goToPage('myAccount')}
        >
          My Account
        </button>
        <button
          style={{
            padding: '10px 20px',
            fontFamily: 'Times New Roman, Times, serif',
            fontSize: '16px',
            fontWeight: 'bold',
            border: '2px solid black',
            borderRadius: '5px',
            // backgroundColor: '#DC2626',
            backgroundImage: `url(${buttonImage})`,
            backgroundSize: 'cover',
            color: 'white',
            boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
          }}
          onClick={() => supabase.auth.signOut()}
        >
          Logout
        </button>
      </footer>
    </div>
  );
};

export default Home;